/**
 * Module « aide » — LA DOCUMENTATION, DANS L’APPLICATION, SUR `/aide`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * OÙ ELLE VIT, ET POURQUOI PAS AILLEURS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Servie par le même artefact que le produit. Pas de site statique séparé, pas
 * d’outil de plus. Trois raisons, dans cet ordre (PLAN-DOCUMENTATION §2) :
 *
 *  1. **le lien contextuel ne peut pas mentir.** Une aide déployée avec le code
 *     décrit la version déployée ; un site tiers décrit la version qu’on a
 *     pensé à republier ;
 *  2. **elle suit le thème de l’instance.** Une salle qui a personnalisé ses
 *     couleurs retrouve les siennes dans son aide, sans rien à configurer ;
 *  3. **elle est versionnée avec le code.** Un écran qui change et son
 *     paragraphe entrent dans le même commit.
 *
 * ★ **SANS GARDE DE SESSION**, comme la boutique et l’inscription. Quelqu’un
 * qui cherche comment retrouver son billet ne doit pas se connecter pour lire
 * la réponse — et la moitié des pages s’adressent justement à des gens qui
 * n’auront jamais de compte.
 *
 * ★ **CHARGÉE PARESSEUSEMENT, COQUE COMPRISE.** Une trentaine de pages de texte
 * français n’ont rien à faire dans le lot initial que téléchargent le guichet
 * chaque matin et le public à chaque achat. Le `<Suspense>` extérieur est posé
 * ici pour la coque ; celui de la coque couvre les pages.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * L’ADRESSE, POUR CEUX QUI VONT LA CITER
 * ────────────────────────────────────────────────────────────────────────────
 *
 *   `/aide`                        le sommaire — six publics, toutes les pages
 *   `/aide/<domaine>/<rubrique>`   une page, par exemple `/aide/campagne/phases`
 *
 * L’ancre `aide/<domaine>/<rubrique>` est l’identifiant STABLE d’une page.
 * L’URL en est la simple concaténation : il n’y a aucune table de
 * correspondance à maintenir, donc aucune à désynchroniser. Un futur lien
 * contextuel depuis un écran d’administration citera l’ancre, jamais une
 * position dans une page.
 */

import { lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router'

import { ChargementPage } from '../../ui/Spinner'

const LayoutAide = lazy(() => import('./LayoutAide').then((m) => ({ default: m.LayoutAide })))
const PageSommaire = lazy(() => import('./PageSommaire').then((m) => ({ default: m.PageSommaire })))
const PageRubrique = lazy(() => import('./PageRubrique').then((m) => ({ default: m.PageRubrique })))

export const routesAide: RouteObject[] = [
  {
    element: (
      <Suspense fallback={<ChargementPage libelle="Chargement de la documentation…" />}>
        <LayoutAide />
      </Suspense>
    ),
    children: [
      { index: true, element: <PageSommaire /> },
      /**
       * ★ Une adresse inconnue rend une page qui le DIT, au lieu de renvoyer en
       * silence au sommaire. Un renvoi muet fait croire à une faute de frappe ;
       * or le cas réel est un lien devenu faux, et il doit se voir.
       */
      { path: ':domaine/:rubrique', element: <PageRubrique /> },
    ],
  },
]

/**
 * ★★ CE BARREL NE RÉEXPORTE PAS `./contenu` — ET C'EST LA RAISON D'ÊTRE DE CE
 * COMMENTAIRE, pour que personne ne « répare » l'oubli apparent.
 *
 * Il portait `export { PAGES, pageParAncre } from './contenu'`. Une seule
 * ligne, et elle coûtait cher : `router.tsx` importe ce barrel pour deux
 * broutilles (`CHEMIN_AIDE`, `routesAide`), ce qui suffisait à entraîner les
 * trente-neuf pages de documentation — 79 ko bruts, 25 ko compressés — dans le
 * LOT INITIAL de **toutes** les pages du produit. Le guichet les téléchargeait
 * chaque matin, le public à chaque achat : +15,6 % sur l'entrée, pour du texte
 * que ni l'un ni l'autre n'ouvrira.
 *
 * Les pages, elles, étaient bien paresseuses. C'est le barrel qui annulait leur
 * paresse — le même piège que celui déjà documenté pour `./components` et
 * `./features/accueil-theatre` en tête de `router.tsx`.
 *
 * Tous les vrais consommateurs (`PageSommaire`, `PageRubrique`,
 * `couverture.test`) importent déjà `./contenu` DIRECTEMENT : la ligne ne
 * servait personne. Si tu as besoin de `PAGES` ou de `pageParAncre`, importe
 * `./contenu` — et vérifie que ton fichier n'est pas, lui, dans le lot initial.
 */
export { CHEMIN_AIDE, urlAide } from './modele'
export type { PageAide, PublicAide } from './modele'
export { PUBLICS } from './sommaire'
